import { useMsal } from '@azure/msal-react';
import { getConfig, tokenRequest } from '../auth/authConfig';

export const useApiClient = () => {
  const { instance, accounts } = useMsal();

  const getAccessToken = async (): Promise<string> => {
    if (!accounts[0]) {
      throw new Error('No account found. Please sign in.');
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...tokenRequest,
        account: accounts[0],
      });
      return response.accessToken;
    } catch (error) {
      console.error('Silent token acquisition failed, falling back to popup', error);
      const response = await instance.acquireTokenPopup(tokenRequest);
      return response.accessToken;
    }
  };

  const apiRequest = async (
    method: string,
    path: string,
    body?: any
  ): Promise<any> => {
    const token = await getAccessToken();
    const config = getConfig();
    // Remove trailing slash from base URL and leading slash from path to avoid double slashes
    const baseUrl = config.API_GATEWAY_URL.replace(/\/$/, '');
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const url = `${baseUrl}${cleanPath}`;

    const headers: HeadersInit = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };

    const options: RequestInit = {
      method,
      headers,
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API request failed: ${response.statusText}`);
    }

    return response.json();
  };

  return {
    get: (path: string) => apiRequest('GET', path),
    post: (path: string, body: any) => apiRequest('POST', path, body),
    put: (path: string, body: any) => apiRequest('PUT', path, body),
    delete: (path: string) => apiRequest('DELETE', path),
  };
};
